import React, { useEffect, useState } from "react";
import { useParams, Link, useOutletContext } from "react-router-dom";
import api, { API } from "@/lib/api";
import { paymentStatusClass } from "@/lib/statusBadge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileDown, Check, Package, Truck, Home as HomeIcon } from "lucide-react";

const STEPS = [
  { key: "pending", label: "Pending", icon: Check },
  { key: "confirmed", label: "Confirmed", icon: Check },
  { key: "packed", label: "Packed", icon: Package },
  { key: "out_for_delivery", label: "Out for delivery", icon: Truck },
  { key: "delivered", label: "Delivered", icon: HomeIcon },
];

export default function OrderDetail() {
  const { brand } = useOutletContext();
  const { id, brand: key } = useParams();
  const dark = brand.key === "blackpekoe";
  const fg = dark ? "#F5F2EB" : "#2D2622";
  const muted = dark ? "rgba(245,242,235,0.7)" : "#5C5753";
  const subtle = dark ? "rgba(245,242,235,0.55)" : "#8C8581";
  const cardBg = dark ? "rgba(255,255,255,0.04)" : "white";
  const cardBorder = dark ? "rgba(255,255,255,0.08)" : "rgba(45,38,34,0.1)";

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/orders/${id}`).then(({ data }) => setOrder(data)).finally(() => setLoading(false));
  }, [id]);

  const downloadInvoice = async () => {
    const token = localStorage.getItem("bbf_token");
    const res = await fetch(`${API}/orders/${id}/invoice`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `invoice-${order.order_no}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  if (loading) return <p className="p-10" style={{ color: subtle }}>Loading…</p>;
  if (!order) return <p className="p-10" style={{ color: fg }}>Order not found.</p>;

  const currentIdx = STEPS.findIndex((s) => s.key === order.status);

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10" style={{ color: fg }}>
      <Link to={`/b/${key}/orders`} className="inline-flex items-center text-sm hover:underline" style={{ color: muted }} data-testid="back-to-orders">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to orders
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="overline" style={{ color: subtle }}>{brand.name} • Order</p>
          <h1 className="font-heading font-bold text-3xl mt-2">{order.order_no}</h1>
          <p className="text-sm mt-1" style={{ color: subtle }}>Placed {new Date(order.created_at).toLocaleString()}</p>
        </div>
        <Button data-testid="invoice-btn" onClick={downloadInvoice} variant="outline"><FileDown className="w-4 h-4 mr-2" />Download invoice</Button>
      </div>

      {order.status !== "cancelled" && (
        <div className="mt-8 rounded-xl p-6" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
          <p className="overline mb-6" style={{ color: subtle }}>Delivery progress</p>
          <div className="flex justify-between items-center">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const done = i <= currentIdx;
              return (
                <React.Fragment key={s.key}>
                  <div className="flex flex-col items-center text-center" data-testid={`step-${s.key}`}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center transition"
                      style={{ background: done ? brand.primary : (dark ? "rgba(255,255,255,0.06)" : "#F5F2EB"), color: done ? "white" : subtle }}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <p className="text-xs mt-2 max-w-[80px]" style={{ color: done ? fg : subtle, fontWeight: done ? 600 : 400 }}>{s.label}</p>
                  </div>
                  {i < STEPS.length - 1 && <div className="flex-1 h-0.5 mx-1" style={{ background: i < currentIdx ? brand.primary : (dark ? "rgba(255,255,255,0.06)" : "#F5F2EB") }} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 rounded-xl p-6" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
          <h3 className="font-heading font-semibold text-lg mb-4">Items</h3>
          <div className="space-y-3">
            {order.items.map((it) => (
              <div key={it.product_id} className="flex justify-between items-center py-3 last:border-0" style={{ borderBottom: `1px solid ${cardBorder}` }}>
                <div>
                  <p className="font-semibold">{it.name}</p>
                  <p className="text-xs" style={{ color: subtle }}>{it.quantity} × ₹{it.price.toFixed(2)} / {it.unit}</p>
                </div>
                <p className="font-semibold">₹{it.subtotal.toFixed(2)}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-4 space-y-2 text-sm" style={{ borderTop: `1px solid ${cardBorder}` }}>
            <div className="flex justify-between"><span style={{ color: muted }}>Subtotal</span><span>₹{order.subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span style={{ color: muted }}>Tax</span><span>₹{order.tax.toFixed(2)}</span></div>
            <div className="flex justify-between"><span style={{ color: muted }}>Delivery</span><span>{order.delivery_fee === 0 ? "Free" : `₹${order.delivery_fee.toFixed(2)}`}</span></div>
            {Number(order.wallet_amount_used || 0) > 0 && (
              <>
                <div className="flex justify-between" style={{ color: "#3D6B42" }}><span>Wallet credit applied</span><span>− ₹{Number(order.wallet_amount_used).toFixed(2)}</span></div>
                <div className="flex justify-between"><span style={{ color: muted }}>Amount payable</span><span>₹{Number(order.amount_payable || 0).toFixed(2)}</span></div>
              </>
            )}
            <div className="flex justify-between pt-2 items-baseline" style={{ borderTop: `1px solid ${cardBorder}` }}>
              <span className="font-heading font-semibold">Total</span>
              <span className="font-heading font-bold text-xl" style={{ color: brand.primary }}>₹{order.total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl p-6" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
            <p className="overline mb-3" style={{ color: subtle }}>Status</p>
            <div className="space-y-2">
              <div className="flex justify-between text-sm"><span style={{ color: muted }}>Order status</span>
                <span className={`status-badge status-${order.status}`}>{order.status.replace("_", " ")}</span></div>
              <div className="flex justify-between text-sm"><span style={{ color: muted }}>Payment</span>
                <span className={`status-badge ${paymentStatusClass(order.payment_status)}`}>{order.payment_status}</span></div>
              {order.razorpay_payment_id && (
                <div className="text-xs pt-2" style={{ borderTop: `1px solid ${cardBorder}`, color: subtle }}>
                  <p className="overline mb-1">Transaction ID</p>
                  <p className="font-mono break-all" data-testid="order-txn-id" style={{ color: fg }}>{order.razorpay_payment_id}</p>
                  {order.razorpay_order_id && (
                    <p className="font-mono text-[10px] mt-1 break-all">RZP order: {order.razorpay_order_id}</p>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="rounded-xl p-6" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
            <p className="overline mb-3" style={{ color: subtle }}>Delivery address</p>
            <p className="font-semibold">{order.address.name}</p>
            <p className="text-sm" style={{ color: muted }}>{order.address.phone}</p>
            <p className="text-sm mt-2" style={{ color: muted }}>
              {order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ""}<br />
              {order.address.city}, {order.address.state} — {order.address.pincode}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
